import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import {
  Truck, TrendingUp, FileText, ShieldCheck, Zap, ScanLine,
  Check, ArrowRight, Fuel, CalendarClock, Users,
} from 'lucide-react';

/**
 * Public marketing home at `/`. Signed-in users are redirected to /dashboard
 * by the HomeRoute wrapper in App.js.
 */
const LandingPage = () => {
  // Capture `?ref=xxx` from marketing links and park it in sessionStorage so
  // the Register form can send it with the signup payload. We skip empty and
  // very long values so this can't be used as an open text channel.
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const ref = (params.get('ref') || '').trim();
      if (ref && ref.length <= 64) sessionStorage.setItem('fc_ref', ref);
    } catch { /* ignore */ }
  }, []);

  return (
  <div className="min-h-screen bg-white text-slate-900">
    {/* ── Nav ─────────────────────────────────────────────────────────── */}
    <nav className="sticky top-0 z-50 bg-white/85 backdrop-blur border-b border-slate-100">
      <div className="max-w-6xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2" data-testid="nav-logo">
          <img src="/fleetcost-icon-192.png" alt="" className="w-8 h-8 object-contain" />
          <span className="font-display font-black text-lg tracking-tight">
            FleetCost<span className="text-indigo-600">.App</span>
          </span>
        </Link>
        <div className="flex items-center gap-3">
          <a href="#pricing" className="hidden sm:inline text-sm text-slate-600 hover:text-slate-900">Pricing</a>
          <a href="#features" className="hidden sm:inline text-sm text-slate-600 hover:text-slate-900">Features</a>
          <Link to="/login" className="text-sm text-slate-600 hover:text-slate-900" data-testid="nav-login">Sign in</Link>
          <Link to="/register" data-testid="nav-signup">
            <Button className="bg-indigo-600 hover:bg-indigo-700 text-white h-9 px-4">Start free trial</Button>
          </Link>
        </div>
      </div>
    </nav>

    {/* ── Hero ────────────────────────────────────────────────────────── */}
    <section className="relative overflow-hidden bg-slate-950 text-white">
      <img
        src="https://images.unsplash.com/photo-1601584115197-04ecc0da31d7?ixlib=rb-4.0.3&auto=format&fit=crop&w=1600&q=80"
        alt=""
        className="absolute inset-0 w-full h-full object-cover opacity-40"
      />
      <div className="absolute inset-0 bg-gradient-to-br from-slate-950/95 via-slate-950/70 to-slate-950/95" />
      <div className="relative max-w-6xl mx-auto px-6 py-24 lg:py-32 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs font-mono tracking-[0.25em] uppercase text-indigo-400 mb-4" data-testid="hero-eyebrow">
            [ Built for African fleets ]
          </p>
          <h1 className="font-display text-5xl lg:text-6xl font-black leading-[0.98] tracking-tight" data-testid="hero-headline">
            Every kilometre.<br />Every litre.<br />
            <span className="text-indigo-400">Accounted for.</span>
          </h1>
          <p className="mt-6 text-lg text-slate-300 max-w-lg">
            FleetCost.App turns fuel slips, service invoices and licence dates into one honest number:
            what your fleet actually costs. No spreadsheets. No guesswork.
          </p>
          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/register" data-testid="hero-cta-signup">
              <Button className="bg-indigo-600 hover:bg-indigo-700 text-white h-12 px-6 text-base">
                Start free trial <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
            <Link to="/login" data-testid="hero-cta-login">
              <Button variant="outline" className="h-12 px-6 text-base bg-white/5 border-white/20 text-white hover:bg-white/10">
                Sign in
              </Button>
            </Link>
          </div>
          <p className="mt-4 text-xs text-slate-400">
            3-month free trial · No card required · Cancel anytime
          </p>
        </div>
        <div className="hidden lg:block">
          <div className="rounded-md border border-white/10 bg-white/5 backdrop-blur p-6 space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="font-mono text-emerald-400">● LIVE</span>
              <span className="text-slate-400">Fleet Health · Yesterday</span>
            </div>
            {[
              { label: 'Fleet spend · Sept 2026', v: 'R 84,230', c: 'text-white' },
              { label: 'Fleet avg km/L', v: '9.2 km/L', c: 'text-emerald-400' },
              { label: 'Overdue services', v: '1', c: 'text-red-400' },
              { label: 'Licences renewing in 30d', v: '2', c: 'text-amber-400' },
              { label: 'Data quality score', v: '99.4%', c: 'text-emerald-400' },
            ].map((r) => (
              <div key={r.label} className="flex items-center justify-between text-sm border-b border-white/10 pb-2 last:border-0">
                <span className="text-slate-300">{r.label}</span>
                <span className={`font-mono font-bold ${r.c}`}>{r.v}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </section>

    {/* ── Feature grid ────────────────────────────────────────────────── */}
    <section id="features" className="max-w-6xl mx-auto px-6 py-20">
      <div className="max-w-2xl mb-12">
        <p className="text-xs font-mono uppercase tracking-widest text-indigo-600">Features</p>
        <h2 className="mt-2 text-3xl lg:text-4xl font-heading font-bold">Everything a fleet manager needs, nothing they don't.</h2>
      </div>
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { icon: Fuel, title: 'Fuel efficiency, per vehicle', body: 'Track km/L (or L/hr for forklifts) automatically from every fill-up. Instant colour-coded alerts when a vehicle drops 20% below its target.' },
          { icon: TrendingUp, title: 'Cost breakdown that adds up', body: 'Fuel, tyres, service, labour, incidents — grouped by vehicle, driver, supplier, month. Export to Excel or PDF for the boardroom.' },
          { icon: CalendarClock, title: 'Never miss a renewal', body: 'Vehicle licences, insurance, driver PDPs — 90/60/30-day countdown tiers with a cash-needed total.' },
          { icon: ShieldCheck, title: 'Compliance dashboard', body: 'One glance shows every non-compliant item across your fleet with the exact document that expired.' },
          { icon: ScanLine, title: 'Invoice OCR (GPT-4o)', body: 'Snap a photo of a fuel slip or service invoice. Amount, litres, odometer and supplier lifted straight into a cost entry.' },
          { icon: Users, title: 'Team seats & roles', body: 'Invite drivers, admin staff or the accountant with granular permissions — view, capture-only, or full access.' },
          { icon: FileText, title: 'Daily digest PDF', body: 'Every morning at 06:00 SAST an executive summary lands in your inbox — spend, overdue services, incidents, renewals.' },
          { icon: Zap, title: 'Incident tracking', body: 'Log breakdowns, accidents and third-party details. Auto-linked to the vehicle and driver, ready for insurance.' },
          { icon: Truck, title: 'Multi-fleet ready', body: 'Trailers, bakkies, trucks, forklifts and private vehicles — each with the right metering (km or hours) and its own targets.' },
        ].map(({ icon: Icon, title, body }) => (
          <div key={title} className="p-6 rounded-md border border-slate-200 bg-white hover:border-indigo-300 hover:shadow-sm transition-all">
            <Icon className="w-6 h-6 text-indigo-600 mb-4" />
            <h3 className="font-heading font-semibold text-slate-900 mb-2">{title}</h3>
            <p className="text-sm text-slate-600 leading-relaxed">{body}</p>
          </div>
        ))}
      </div>
    </section>

    {/* ── Pricing ─────────────────────────────────────────────────────── */}
    <section id="pricing" className="bg-slate-50 border-y border-slate-200">
      <div className="max-w-6xl mx-auto px-6 py-20">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-mono uppercase tracking-widest text-indigo-600">Pricing</p>
          <h2 className="mt-2 text-3xl lg:text-4xl font-heading font-bold">Priced for real African fleets.</h2>
          <p className="mt-2 text-slate-600">Start free for 3 months. No card needed. Cancel any time.</p>
        </div>
        <div className="grid md:grid-cols-3 gap-6">
          {[
            {
              name: 'Solo',
              price: 'R 199',
              period: '/ month',
              blurb: 'For a single owner-operator or a small yard',
              features: ['Up to 5 vehicles', '2 drivers', '1 user seat', 'Daily digest PDF', 'Excel + PDF exports', 'Email support'],
              cta: 'Start free trial',
              popular: false,
            },
            {
              name: 'Team',
              price: 'R 499',
              period: '/ month',
              blurb: 'For a growing fleet with an admin or two',
              features: ['Up to 20 vehicles', 'Unlimited drivers', '5 user seats', 'Invoice OCR', 'Compliance alerts', 'Priority support'],
              cta: 'Start free trial',
              popular: true,
            },
            {
              name: 'Fleet+',
              price: 'R 999',
              period: '/ month',
              blurb: 'For serious operators with 20+ vehicles',
              features: ['Unlimited vehicles', 'Unlimited seats', 'API access', 'Multi-branch reports', 'Onboarding call', 'Dedicated success manager'],
              cta: 'Start free trial',
              popular: false,
            },
          ].map((p) => (
            <div
              key={p.name}
              className={`rounded-md p-6 flex flex-col ${p.popular ? 'border-2 border-indigo-600 bg-white shadow-lg scale-[1.02]' : 'border border-slate-200 bg-white'}`}
              data-testid={`pricing-${p.name.toLowerCase()}`}
            >
              {p.popular && (
                <span className="self-start px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-700 mb-3">
                  Most popular
                </span>
              )}
              <h3 className="font-heading text-2xl font-bold">{p.name}</h3>
              <p className="text-sm text-slate-600 mt-1">{p.blurb}</p>
              <div className="mt-6">
                <span className="text-4xl font-heading font-black">{p.price}</span>
                <span className="text-slate-500 ml-1">{p.period}</span>
              </div>
              <ul className="mt-6 space-y-2 text-sm text-slate-700 flex-1">
                {p.features.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0" /> {f}
                  </li>
                ))}
              </ul>
              <Link to="/register" className="mt-6">
                <Button
                  className={`w-full h-11 ${p.popular ? 'bg-indigo-600 hover:bg-indigo-700 text-white' : 'bg-slate-900 hover:bg-slate-800 text-white'}`}
                >
                  {p.cta}
                </Button>
              </Link>
            </div>
          ))}
        </div>
        <p className="mt-8 text-center text-xs text-slate-500">
          Prices exclude VAT. All plans include unlimited cost entries, incidents, licences and daily digest emails.
        </p>
      </div>
    </section>

    {/* ── CTA strip ───────────────────────────────────────────────────── */}
    <section className="max-w-4xl mx-auto px-6 py-20 text-center">
      <h2 className="text-3xl lg:text-4xl font-heading font-bold">Stop guessing what your fleet costs.</h2>
      <p className="mt-3 text-slate-600 max-w-xl mx-auto">
        Sign up in under 2 minutes. Load a demo fleet with one click to see the product in action, then wipe it and add your real vehicles.
      </p>
      <div className="mt-8">
        <Link to="/register" data-testid="cta-strip-signup">
          <Button className="bg-indigo-600 hover:bg-indigo-700 text-white h-12 px-8 text-base">
            Start your free trial <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
        </Link>
      </div>
    </section>

    {/* ── Footer ──────────────────────────────────────────────────────── */}
    <footer className="border-t border-slate-200 bg-slate-50">
      <div className="max-w-6xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-slate-500">
        <div>
          © 2026 <strong className="text-slate-700">Fleet Cost</strong> · 52 Buffel Str, Hoedspruit, 1380, South Africa
        </div>
        <div className="flex items-center gap-4">
          <Link to="/terms" className="hover:text-slate-900" data-testid="footer-terms">Terms</Link>
          <Link to="/privacy" className="hover:text-slate-900" data-testid="footer-privacy">Privacy</Link>
          <a href="mailto:hpbza@icloud.com" className="hover:text-slate-900">Contact</a>
        </div>
      </div>
    </footer>
  </div>
  );
};

export default LandingPage;
